import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, CreditCard, Crown, Clock, Users, Shield, Video } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = ({ user, setUser }) => {
  const [captionsEnabled, setCaptionsEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [subStatus, setSubStatus] = useState(user?.subscription_status || 'inactive');
  const [trialEnds, setTrialEnds] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [planInfo, setPlanInfo] = useState({ plan_type: user?.plan_type || 'basic', plan_name: 'Basic Host', max_guests: 1, price: 9.99 });
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
    fetchSubscriptionStatus();
    fetchPlanInfo();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('kaplie_token');
      const response = await axios.get(`${API}/user/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCaptionsEnabled(response.data.captionsEnabled);
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('kaplie_token');
      const res = await axios.get(`${API}/stripe/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubStatus(res.data.subscription_status);
      setTrialEnds(res.data.trial_ends_at);
    } catch (e) {
      console.error('Failed to fetch subscription status');
    }
  };

  const fetchPlanInfo = async () => {
    try {
      const token = localStorage.getItem('kaplie_token');
      const res = await axios.get(`${API}/stripe/plan-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlanInfo(res.data);
    } catch (e) {}
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('kaplie_token');
      await axios.put(
        `${API}/user/settings`,
        { captionsEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updatedUser = { ...user, captionsEnabled };
      localStorage.setItem('kaplie_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const token = localStorage.getItem('kaplie_token');
      const res = await axios.post(`${API}/stripe/customer-portal`, {
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error('Failed to open subscription manager');
    } finally {
      setPortalLoading(false);
    }
  };

  const getTrialDaysLeft = () => {
    if (!trialEnds) return null;
    const end = new Date(trialEnds);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const trialDays = getTrialDaysLeft();

  const getStatusDisplay = () => {
    if (subStatus === 'trialing') {
      return { label: 'Free Trial', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' };
    }
    if (subStatus === 'active') {
      return { label: 'Active', color: 'text-[#D4AF37]', bg: 'bg-[#D4AF37]/10 border-[#D4AF37]/20' };
    }
    if (subStatus === 'past_due') {
      return { label: 'Past Due', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' };
    }
    return { label: 'Inactive', color: 'text-zinc-500', bg: 'bg-white/[0.03] border-white/[0.06]' };
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div className="min-h-screen bg-[#050505] text-white font-inter">
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#D4AF37]/[0.03] rounded-full blur-[150px]" />
      </div>

      <nav className="relative z-10 border-b border-white/[0.06] backdrop-blur-2xl bg-black/60 sticky top-0">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-4 flex items-center gap-3">
          <Link to="/dashboard">
            <button className="p-2 hover:bg-white/[0.06] rounded-lg transition-colors" data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-5 h-5 text-zinc-400" />
            </button>
          </Link>
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center">
              <Video className="w-3.5 h-3.5 text-black" />
            </div>
            <h1 className="font-satoshi font-bold text-lg tracking-tight">Settings</h1>
          </div>
        </div>
      </nav>

      <main className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 py-10 space-y-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37]/80 mb-2">Account</p>
          <h2 className="font-satoshi font-bold text-3xl tracking-tight">Your preferences</h2>
        </div>

        {/* Subscription */}
        <div className="p-7 rounded-2xl backdrop-blur-2xl bg-black/60 border border-white/[0.08] shadow-[0_8px_32px_rgba(0,0,0,0.4)]" data-testid="subscription-section">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
              <Crown className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <h3 className="font-satoshi font-bold text-lg">Subscription</h3>
          </div>

          <div className="flex items-center justify-between mb-5">
            <div>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase border ${statusDisplay.bg} ${statusDisplay.color}`}>
                {statusDisplay.label}
              </span>
              {subStatus === 'trialing' && trialDays !== null && (
                <div className="flex items-center gap-2 mt-3 text-sm text-zinc-500">
                  <Clock className="w-4 h-4" />
                  <span>{trialDays} day{trialDays !== 1 ? 's' : ''} left in trial</span>
                </div>
              )}
            </div>
          </div>

          {/* Current Plan */}
          <div className="p-5 rounded-xl bg-white/[0.03] border border-white/[0.06] mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {planInfo.plan_type === 'pro' ? (
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center shadow-[0_0_15px_rgba(212,175,55,0.2)]">
                    <Crown className="w-4 h-4 text-black" />
                  </div>
                ) : (
                  <div className="w-9 h-9 rounded-xl bg-white/[0.06] flex items-center justify-center">
                    <Shield className="w-4 h-4 text-zinc-300" />
                  </div>
                )}
                <div>
                  <p className="font-semibold text-white">{planInfo.plan_name}</p>
                  <p className="text-sm text-zinc-500">${planInfo.price}/month</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <Users className="w-4 h-4" />
                <span>{planInfo.max_guests} guest{planInfo.max_guests !== 1 ? 's' : ''}/call</span>
              </div>
            </div>
          </div>

          {planInfo.plan_type === 'basic' && (
            <Link to="/subscribe">
              <button className="w-full py-3 mb-3 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all flex items-center justify-center gap-2" data-testid="upgrade-to-pro-btn">
                <Crown className="w-4 h-4" />
                Upgrade to Pro — 5 Guests / $17.99
              </button>
            </Link>
          )}

          <button
            onClick={handleManageSubscription}
            disabled={portalLoading}
            data-testid="manage-subscription-btn"
            className="w-full py-3 bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] rounded-full font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {portalLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <CreditCard className="w-4 h-4" />
                Manage subscription
              </>
            )}
          </button>
        </div>

        {/* Captions Toggle */}
        <div className="p-7 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
          <h3 className="font-satoshi font-bold text-lg mb-5">Call preferences</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium mb-1">Real-time captions</p>
              <p className="text-sm text-zinc-500">Enable live AI captions during calls</p>
            </div>
            <button
              onClick={() => setCaptionsEnabled(!captionsEnabled)}
              data-testid="toggle-captions-button"
              className={`w-14 h-8 rounded-full transition-all flex items-center px-1 ${
                captionsEnabled ? 'bg-gradient-to-r from-[#D4AF37] to-[#C5A059]' : 'bg-white/[0.08]'
              }`}
            >
              <div className={`w-6 h-6 bg-white rounded-full shadow-lg transition-all ${
                captionsEnabled ? 'translate-x-6' : 'translate-x-0'
              }`} />
            </button>
          </div>
        </div>

        {/* Account Info */}
        <div className="p-7 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
          <h3 className="font-satoshi font-bold text-lg mb-5">Account info</h3>
          <div className="space-y-4 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-zinc-500">Name</span>
              <span className="text-white font-medium" data-testid="user-name-display">{user?.name}</span>
            </div>
            <div className="h-px bg-white/[0.04]" />
            <div className="flex justify-between items-center">
              <span className="text-zinc-500">Email</span>
              <span className="text-white font-medium" data-testid="user-email-display">{user?.email}</span>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={loading}
          data-testid="save-settings-button"
          className="w-full py-3.5 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" />
          {loading ? 'Saving…' : 'Save changes'}
        </button>
      </main>
    </div>
  );
};

export default SettingsPage;
